import { createFileRoute } from "@tanstack/react-router";
import {
  LiveChart,
  LiveRatesTable,
  HindiWeightCalc,
  InHiLinkMatrix,
  FaqAccordion,
  SilverStrip,
  KaratComparisonTable,
  buildInHiSchema,
  HindiUniqueBlock,
  hiDateParts,
  goldRatesQueryOptions,
} from "@/components/in-hi/shell";

const SLUG = "/in/hi/aaj-ka-sona-chandi-bhav";
const CANONICAL = `https://drhint.com${SLUG}`;

const FAQS = [
  { q: "आज का सोना और चांदी का भाव क्या है?", a: "भारत के सराफा बाजार में आज सोने और चांदी दोनों का लाइव भाव ऊपर दी गयी तालिका में प्रति ग्राम, प्रति 10 ग्राम, प्रति तोला और प्रति किलो के हिसाब से अपडेट कर दिया गया है — यह हर 30 मिनट पर अंतरराष्ट्रीय स्पॉट रेट और USD/INR से जुड़ा रहता है।" },
  { q: "22 कैरेट सोने का भाव आज का लाइव अपडेट कहां देखें?", a: "इसी पेज पर ऊपर दी गयी 'लाइव भाव तालिका' में 22K, 24K और 21K तीनों कैरेट के दाम प्रति ग्राम और प्रति 10 ग्राम में रुपये में दिखाए गये हैं। बाजार खुला हो या बंद, यह पेज हर 30 मिनट पर स्वचालित अपडेट होता रहता है।" },
  { q: "डिजिटल गोल्ड और फिजिकल गोल्ड के आज के भाव में क्या अंतर है?", a: "डिजिटल गोल्ड आमतौर पर 24K स्पॉट रेट पर 3% GST जोड़कर बेचा जाता है और उसमें मेकिंग चार्ज नहीं होता। फिजिकल आभूषण में 22K पर मेकिंग चार्ज (₹350–₹700 प्रति ग्राम) और 5% अलग GST जुड़ती है, इसीलिए फिजिकल दाम डिजिटल से 10–15% अधिक पड़ता है।" },
  { q: "सोnaा क्या रेट है आज का और इस पर कितना प्रतिशत जीएसटी (GST) लगता है?", a: "आज कच्चे सोने पर 3% GST लगती है। यदि आप गहने खरीद रहे हैं तो मेकिंग चार्ज पर अतिरिक्त 5% GST देनी होती है। ऊपर दी गयी कीमत केवल कच्चे धातु की है — बिल में GST + मेकिंग जुड़ने पर कुल लागत बढ़ जाती है।" },
];

export const Route = createFileRoute("/in/hi/aaj-ka-sona-chandi-bhav")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    return null;
  },
  head: () => {
    const dp = hiDateParts();
    const title = `सोना चांदी आज का भाव: 22 और 24 कैरेट लाइव रेट`;
    const description = `भारत में आज का सोना चांदी का भाव लाइव देखें। 22 कैरेट और 24 कैरेट सोने का आज का रेट, अहमदाबाद, कानपुर, हरियाणा और राजस्थान के भावों के साथ रोजाना अपडेटेड।`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "hi-IN" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "hi_IN" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "hi-IN", href: CANONICAL },
        { rel: "alternate", hreflang: "en-IN", href: "https://drhint.com/gold-rate-india" },
      ],
      scripts: [{ type: "application/ld+json", children: JSON.stringify(buildInHiSchema({ slug: SLUG, name: title, description, faqs: FAQS, breadcrumbLabel: "आज का सोना-चांदी दैनिक भाव" })) }],
    };
  },
  component: Page,
});

function Page() {
  const dp = hiDateParts();
  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">भारत · लाइव सराफा</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          आज का सोना-चांदी का भाव: 22 कैरेट और 24 कैरेट सोने का लाइव रेट ({dp.day}, {dp.dayNum} {dp.month} {dp.year})
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          यह पेज भारत के सराफा बाजार में <strong>सोना चांदी आज का भाव</strong> हर 30 मिनट पर अंतरराष्ट्रीय स्पॉट रेट से जोड़कर दिखाता है। पूरी रेंज — प्रति ग्राम से लेकर प्रति किलो तक — नीचे लाइव तालिका में उपलब्ध है।
        </p>
      </header>
      <HindiUniqueBlock slug={SLUG} />
      <LiveChart title={`सोने का 30-दिन ट्रेंड — ${dp.month} ${dp.year}`} />
      <LiveRatesTable />
      <HindiWeightCalc />
      <SilverStrip />
      <KaratComparisonTable />
      <FaqAccordion items={FAQS} />
      <InHiLinkMatrix current={SLUG} />
    </main>
  );
}